import type { MouseEventHandler, ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface DetailsDrawerProps {
  title: ReactNode;
  description?: ReactNode;
  icon?: LucideIcon;
  open?: boolean;
  onClose: () => void;
  widthClassName?: string;
  children: ReactNode;
}

export function DetailsDrawer({
  title,
  description,
  icon: Icon,
  open = true,
  onClose,
  widthClassName,
  children,
}: DetailsDrawerProps) {
  return (
    <Sheet open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <SheetContent
        side="right"
        className={cn('w-full sm:max-w-[680px] md:max-w-[760px]', widthClassName)}
      >
        <SheetHeader>
          <div className="flex items-start gap-3 pr-8">
            {Icon && (
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Icon className="h-5 w-5" aria-hidden />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <SheetTitle className="break-words">{title}</SheetTitle>
              {description && (
                <SheetDescription asChild>
                  <div className="mt-1 flex flex-wrap items-center gap-2">{description}</div>
                </SheetDescription>
              )}
            </div>
          </div>
        </SheetHeader>
        {children}
      </SheetContent>
    </Sheet>
  );
}

export function DetailsPanel({
  title,
  description,
  icon: Icon,
  children,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  icon?: LucideIcon;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('overflow-hidden rounded-lg border border-border bg-card', className)}>
      <div className="border-b border-border px-4 py-4">
        <div className="flex items-start gap-3">
          {Icon && (
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Icon className="h-5 w-5" aria-hidden />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <h2 className="break-words text-[15px] font-semibold leading-tight">{title}</h2>
            {description && (
              <div className="mt-1 flex flex-wrap items-center gap-2 text-[12px] text-muted-foreground">
                {description}
              </div>
            )}
          </div>
        </div>
      </div>
      {children}
    </section>
  );
}

export function DetailsDrawerBody({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex-1 overflow-y-auto px-5 py-4 text-[13px]', className)}>
      {children}
    </div>
  );
}

export function DetailsPanelBody({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={cn('px-4 py-4 text-[13px]', className)}>{children}</div>;
}

export function DetailsActionBar({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={cn('mb-4 flex flex-wrap gap-2', className)}>{children}</div>;
}

export function DetailsDrawerSkeleton() {
  return (
    <div className="space-y-3 p-5">
      <div className="h-7 w-2/3 animate-pulse rounded bg-muted" />
      <div className="h-28 animate-pulse rounded bg-muted" />
      <div className="h-64 animate-pulse rounded bg-muted" />
    </div>
  );
}

export function DetailsDrawerError({
  error,
  fallback = 'Could not load details.',
}: {
  error: unknown;
  fallback?: string;
}) {
  return (
    <div className="p-5 text-sm text-destructive">
      {error instanceof Error ? error.message : fallback}
    </div>
  );
}

export function DetailSectionHeading({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'text-[11px] font-semibold uppercase tracking-[0.6px] text-muted-foreground',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function DetailGroup({
  title,
  children,
  className,
}: {
  title: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('space-y-2', className)}>
      <DetailSectionHeading>{title}</DetailSectionHeading>
      {children}
    </section>
  );
}

export function DetailRow({ label, value }: { label: string; value?: ReactNode }) {
  if (value === undefined || value === null || value === '') return null;
  return (
    <div className="grid grid-cols-[88px_minmax(0,1fr)] gap-2 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="min-w-0 break-words">{value}</span>
    </div>
  );
}

export function DetailFact({ label, value }: { label: string; value?: ReactNode }) {
  return (
    <div>
      <DetailSectionHeading className="text-[10px]">{label}</DetailSectionHeading>
      <div className="mt-0.5 break-words text-[13px] font-medium text-foreground">
        {value || '-'}
      </div>
    </div>
  );
}

export function DetailFactGrid({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <Card className={cn('grid grid-cols-1 gap-3 p-3 sm:grid-cols-2', className)}>
      {children}
    </Card>
  );
}

export function JsonPreview({ value }: { value: unknown }) {
  return (
    <pre className="max-h-72 overflow-auto rounded-md bg-muted p-3 text-[11px] leading-relaxed text-muted-foreground">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

export function PlaceholderActionButton({
  children,
  onClick,
}: {
  children: ReactNode;
  onClick?: MouseEventHandler<HTMLButtonElement>;
}) {
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      onClick={onClick}
      className="border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 hover:text-amber-950 dark:border-amber-800 dark:bg-amber-950/20 dark:text-amber-200"
    >
      {children}
    </Button>
  );
}
